<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router"; // Import useRoute

const currentRoute = useRoute();

// Define the expected sequence of steps in your booking flow
const bookingFlowSteps = [
  { name: "Search Results", path: "/booking", routeName: "booking" },
  {
    name: "Clinic Profile",
    path: "/booking/clinic-profile",
    routeName: "clinic-profile",
  },
  { name: "Booking Summary", path: "/booking/summary", routeName: "summary" },
  {
    name: "Booking Confirmation",
    path: "booking/confirmation",
    routeName: "confirmation",
  },
  // Add other sequential booking steps here if necessary
];

const generatedCrumbs = computed(() => {
  const crumbs = [];
  const currentRouteName = currentRoute.name;

  // Check if the current route is part of the defined booking flow
  const currentFlowStepIndex = bookingFlowSteps.findIndex(
    (step) => step.routeName === currentRouteName
  );

  if (currentFlowStepIndex !== -1) {
    // If it's part of the flow, add all steps up to and including the current one
    for (let i = 0; i <= currentFlowStepIndex; i++) {
      crumbs.push({
        name: bookingFlowSteps[i].name,
        path: bookingFlowSteps[i].path,
        query: currentRoute.query, // Preserve current query parameters
      });
    }
  } else {
    // Handle routes not in the main booking flow (e.g., /appointments, /settings, /login)
    // These routes should have their own breadcrumb defined in meta.
    currentRoute.matched.forEach((routeRecord) => {
      if (routeRecord.meta && routeRecord.meta.breadcrumb) {
        let name = "";
        if (typeof routeRecord.meta.breadcrumb === "function") {
          name = routeRecord.meta.breadcrumb(currentRoute);
        } else {
          name = routeRecord.meta.breadcrumb;
        }
        const path = routeRecord.path;
        // Add the crumb if a name was resolved and it's not already in the list
        if (name && !crumbs.some((c) => c.path === path && c.name === name)) {
          crumbs.push({
            name,
            path,
            query: currentRoute.query, // Preserve current query parameters
          });
        }
      }
    });

    // If crumbs were populated from meta (for non-flow pages)
    // and the first crumb isn't "Search Results" (i.e., the current page is not 'booking' itself),
    // then prepend "Search Results". This ensures pages like "My Appointments" are shown as "Home > Search Results > My Appointments".
    // Pages like "Login" which have no breadcrumb meta will result in an empty `crumbs` array here.
    if (
      crumbs.length > 0 &&
      (crumbs[0].name !== "Search Results" || crumbs[0].path !== "/")
    ) {
      crumbs.unshift({
        name: "Search Results",
        path: "/",
        query: currentRoute.query, // Preserve current query parameters
      });
    }
  }

  return crumbs;
});
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-1 mb-40 text-14 md:text-16"
    id="breadcrumbs">
    <!-- This div will always render, showing at least "Home" -->
    <!-- Home link to main domain root -->
    <a href="/" class="hover:text-blue hover:underline">Home</a>

    <!-- Separator and then the rest of the breadcrumbs from Vue Router -->
    <template v-if="generatedCrumbs.length > 0">
      <i class="ri-arrow-right-s-line mx-1 text-grey-dark"></i>
      <template
        v-for="(crumb, index) in generatedCrumbs"
        :key="crumb.path + '-' + index">
        <router-link
          v-if="index < generatedCrumbs.length - 1"
          :to="{ path: crumb.path, query: crumb.query }"
          class="hover:text-blue hover:underline">
          {{ crumb.name }}
        </router-link>
        <span v-else aria-current="page" class="text-grey-dark breadcrumb_last">
          {{ crumb.name }}
        </span>
        <i
          v-if="index < generatedCrumbs.length - 1"
          class="ri-arrow-right-s-line mx-1 text-grey-dark"></i>
      </template>
    </template>
  </div>
</template>
